import React, { ButtonHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'danger'
  | 'success';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  className,
  variant = 'primary',
  size = 'medium',
  children,
  disabled,
  ...props
}) => {
  const baseStyles =
    'inline-flex items-center justify-center rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  const variants: Record<ButtonVariant, string> = {
    primary:
      'bg-primary text-primary-foreground hover:bg-primary-hover focus:outline-none focus:ring-0',
    secondary:
      'bg-secondary text-secondary-foreground hover:bg-secondary-hover focus:outline-none focus:ring-0',
    outline:
      'border border-border bg-background text-foreground hover:bg-muted focus:outline-none focus:ring-0',
    ghost:
      'text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-0',
    danger:
      'bg-destructive text-destructive-foreground hover:bg-destructive-hover focus:outline-none focus:ring-0',
    success:
      'bg-success text-success-foreground hover:bg-success-hover focus:outline-none focus:ring-0',
  };

  const sizes: Record<ButtonSize, string> = {
    small: 'px-3 py-1.5 text-sm',
    medium: 'px-4 py-2 text-sm',
    large: 'px-6 py-3 text-base',
  };

  return (
    <button
      type={props.type || 'button'}
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
